/** Human-readable rendering of wire values. Pure; shared by the pages and the tests. */

import { GRADE_LABEL, SYMPTOM_LABEL, TEMP_CLASS_LABEL, classifyTemp } from '@oncocoach/care';
import type { GradedSymptom, TempClass, TempReading } from './types';

export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

export function shortAddress(address: string): string {
  return address.length > 14 ? `${address.slice(0, 10)}…${address.slice(-8)}` : address;
}

const ADDRESS_LIKE = /^0x[0-9a-fA-F]{4,}(…|\.{3})?[0-9a-fA-F]*$/;

export function looksLikeAddress(value: string | null | undefined): boolean {
  const v = value?.trim() ?? '';
  return v !== '' && ADDRESS_LIKE.test(v);
}

export function monogram(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0 || looksLikeAddress(name)) return '☀';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** "Good morning" / "Good afternoon" / "Good evening" for a local hour. */
export function greeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function localHour(now: number, tzOffsetMinutes: number): number {
  return new Date(now + tzOffsetMinutes * 60_000).getUTCHours();
}

/** One decimal, always: 37 °C and 37.0 °C read differently to someone watching for 38.0. */
export function fmtTemp(celsius: number | null | undefined): string {
  return typeof celsius === 'number' ? `${celsius.toFixed(1)}°` : '—';
}

/** The same temperature in Fahrenheit, for the thermometer the patient actually owns. */
export function toFahrenheit(celsius: number): number {
  return Math.round((celsius * 9) / 5 + 32) / 1;
}

export { SYMPTOM_LABEL, GRADE_LABEL, classifyTemp };

export function gradeLabel(grade: number): string {
  return (GRADE_LABEL[Math.max(0, Math.min(4, Math.round(grade)))] as string) ?? '';
}

const TONE: Record<TempClass, 'good' | 'warn' | 'bad'> = { low: 'bad', normal: 'good', watch: 'warn', fever: 'bad' };

export const TEMP_CLASS: Record<TempClass, { label: string; tone: 'good' | 'warn' | 'bad' }> = Object.fromEntries(
  (Object.keys(TEMP_CLASS_LABEL) as TempClass[]).map((k) => [k, { label: TEMP_CLASS_LABEL[k], tone: TONE[k] }]),
) as Record<TempClass, { label: string; tone: 'good' | 'warn' | 'bad' }>;

/** "8:00 AM" from "08:00". */
export function fmtClock(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "Once daily · 8:00 AM" / "Twice daily · 8AM & 6PM". */
export function fmtSchedule(times: readonly string[]): string {
  const words = ['Once', 'Twice', 'Three times', 'Four times', 'Five times', 'Six times'];
  const how = `${words[times.length - 1] ?? `${times.length} times`} daily`;
  if (times.length === 1) return `${how} · ${fmtClock(times[0] as string)}`;
  return `${how} · ${times.map((t) => fmtClock(t).replace(':00 ', '')).join(' & ')}`;
}

/** "2 min ago", "3 h ago", "yesterday", "Sep 4". */
export function fmtAgo(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  return new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function fmtDate(at: number | string): string {
  const d = typeof at === 'string' ? new Date(`${at}T12:00:00Z`) : new Date(at);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function fmtDateTime(at: number): string {
  return new Date(at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function describeSymptom(s: GradedSymptom): string {
  return `${SYMPTOM_LABEL[s.code]} (${gradeLabel(s.grade).toLowerCase()})`;
}

export function describeReading(r: TempReading): string {
  const cls = TEMP_CLASS[classifyTemp(r.celsius)]?.label ?? '';
  const symptoms = r.symptoms.map(describeSymptom);
  return `${fmtTemp(r.celsius)}C · ${cls}${symptoms.length ? ` · ${symptoms.join(', ')}` : ''}`;
}

/** "12" → the fire badge text; `0` shows nothing. */
export function streakBadge(n: number): string | null {
  return n > 0 ? String(n) : null;
}

export function pct(n: number): string {
  return `${Math.round(n)}%`;
}
